import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { forexApi, type AtomicModel } from "@/lib/forex-api";

interface ModelConfig {
  id: string;
  provider: string;
  model: string;
  displayName: string;
  version: string | null;
  status: "active" | "inactive";
  referenceLatencyMs: number | null;
  pricePer1kTokens: number | null;
}

function toModelConfig(item: AtomicModel): ModelConfig {
  return {
    id: item.atomic_model_id,
    provider: item.provider,
    model: item.model_id,
    displayName: item.display_name || item.model_id,
    version: null,
    status: item.status === "enabled" ? "active" : "inactive",
    referenceLatencyMs: Number.isFinite(item.latency_ms) ? item.latency_ms : null,
    pricePer1kTokens: Number.isFinite(item.cost_per_1k_tokens) ? item.cost_per_1k_tokens : null,
  };
}

export default function ForexModelConfigPage() {
  const navigate = useNavigate();
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [remoteReady, setRemoteReady] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);
  const showToast = (msg: string, type: "success" | "error" = "success") => { setToast({ type, msg }); setTimeout(() => setToast(null), 2500); };
  const [statusFilter, setStatusFilter] = useState<string>("all");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    forexApi.listAtomicModels()
      .then((result) => {
        if (cancelled) return;
        setModels(result.items.map(toModelConfig));
        setRemoteReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setRemoteReady(false);
          showToast("后端模型接口不可用，请稍后重试", "error");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const activeModels = models.filter((m) => m.status === "active").length;
  const filteredModels = statusFilter === "all" ? models : models.filter((m) => m.status === statusFilter);
  const resourceCount = loading ? "加载中" : remoteReady ? String(models.length) : "加载失败";
  const observedMetric = loading ? "加载中" : remoteReady ? "未提供" : "加载失败";

  return (
    <div className="space-y-5">
      <Breadcrumb items={[{ label: "管理后台", href: "/forex/overview" }, { label: "智能体运营" }, { label: "原子模型资源管理" }]} />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">原子模型资源管理</h2>
          <p className="text-xs text-text-muted mt-0.5">查看原子模型资源的提供方、模型 ID、限额与状态。策略角色及 Prompt 在策略模板中配置。</p>
          <p className={`text-[10px] mt-1 ${remoteReady ? "text-emerald-500" : "text-amber-500"}`}>
            {loading ? "正在同步后端模型接口..." : remoteReady ? "已接入 /quantum/business/forex/atomic-models" : "后端模型接口加载失败，资源状态未知"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate("/forex/model-usage")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand cursor-pointer whitespace-nowrap"><i className="ri-bar-chart-line text-sm" />模型调用大盘入口</button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { l: "模型总数", v: resourceCount, c: "text-text-primary" },
          { l: "已启用", v: loading ? "加载中" : remoteReady ? String(activeModels) : "加载失败", c: "text-emerald-500" },
          { l: "已禁用", v: loading ? "加载中" : remoteReady ? String(models.length - activeModels) : "加载失败", c: "text-red-500" },
          { l: "本月调用次数", v: observedMetric, c: "text-text-primary" },
          { l: "成功率", v: observedMetric, c: "text-text-primary" },
          { l: "平均延迟", v: observedMetric, c: "text-text-primary" },
        ].map((s) => (
          <div key={s.l} className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center">
            <p className="text-[10px] text-text-muted">{s.l}</p>
            <p className={`text-lg font-semibold font-mono mt-0.5 ${s.c}`}>{s.v}</p>
          </div>
        ))}
        <div className="md:col-span-2 lg:col-span-1"></div>
      </div>

      {/* Cost summary */}
      <div className="bg-white rounded-lg border border-surface-border p-4">
        <h3 className="text-sm font-semibold text-text-primary mb-1">成本统计（本月）</h3>
        <p className="text-[11px] text-text-muted mb-3">月累计费用未接入调用账单；模型单价见下方列表。</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-surface/50 rounded-md p-3 text-center">
            <p className="text-[10px] text-text-muted">总成本</p>
            <p className="text-lg font-semibold font-mono text-text-primary mt-0.5">{observedMetric}</p>
          </div>
          <div className="bg-surface/50 rounded-md p-3 text-center">
            <p className="text-[10px] text-text-muted">OpenAI 成本</p>
            <p className="text-lg font-semibold font-mono text-text-primary mt-0.5">{observedMetric}</p>
          </div>
          <div className="bg-surface/50 rounded-md p-3 text-center">
            <p className="text-[10px] text-text-muted">Anthropic 成本</p>
            <p className="text-lg font-semibold font-mono text-text-primary mt-0.5">{observedMetric}</p>
          </div>
          <div className="bg-surface/50 rounded-md p-3 text-center">
            <p className="text-[10px] text-text-muted">DeepSeek 成本</p>
            <p className="text-lg font-semibold font-mono text-text-primary mt-0.5">{observedMetric}</p>
          </div>
        </div>
      </div>

      {/* Model table */}
      <div className="bg-white rounded-lg border border-surface-border overflow-hidden">
        <div className="px-4 py-3 border-b border-surface-border flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-sm font-semibold text-text-primary">模型列表</h3>
          <div className="flex items-center gap-1 bg-surface rounded-lg p-1">
            {[{ key: "all", label: "全部" }, { key: "active", label: "已启用" }, { key: "inactive", label: "已禁用" }].map((f) => (
              <button key={f.key} onClick={() => setStatusFilter(f.key)} className={`px-3 py-1 rounded-md text-[11px] font-medium transition-colors cursor-pointer whitespace-nowrap ${statusFilter === f.key ? "bg-white text-brand shadow-sm" : "text-text-secondary hover:text-text-primary"}`}>{f.label} {f.key !== "all" && `(${models.filter((m) => m.status === f.key).length})`}</button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] min-w-[1100px]">
            <thead>
              <tr className="border-b border-surface-border bg-surface/50">
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px] whitespace-nowrap">ID</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px] whitespace-nowrap">模型 ID</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px] whitespace-nowrap">提供方</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px] whitespace-nowrap">显示名称 / 版本</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px] whitespace-nowrap">状态</th>
                <th className="text-right px-4 py-2.5 text-text-secondary font-medium text-[11px] whitespace-nowrap">调用次数</th>
                <th className="text-right px-4 py-2.5 text-text-secondary font-medium text-[11px] whitespace-nowrap">成功率</th>
                <th className="text-right px-4 py-2.5 text-text-secondary font-medium text-[11px] whitespace-nowrap">目录延迟参考</th>
                <th className="text-right px-4 py-2.5 text-text-secondary font-medium text-[11px] whitespace-nowrap">每千 Token 单价</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px] whitespace-nowrap">操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredModels.map((m) => (
                <tr key={m.id} className="border-b border-surface-border hover:bg-surface/30 transition-colors">
                  <td className="px-4 py-2.5 text-text-primary font-mono text-[11px]">{m.id}</td>
                  <td className="px-4 py-2.5 text-text-primary text-xs font-medium font-mono whitespace-nowrap">{m.model}</td>
                  <td className="px-4 py-2.5 text-text-secondary text-xs">{m.provider}</td>
                  <td className="px-4 py-2.5 text-text-muted text-[11px]"><span className="text-text-primary">{m.displayName}</span><br />版本：{m.version ?? "未提供"}</td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${m.status === "active" ? "bg-emerald-500/10 text-emerald-500" : "bg-red-500/10 text-red-500"}`}>
                      <i className={`text-[8px] ri-checkbox-blank-circle-fill`} />
                      {m.status === "active" ? "启用" : "禁用"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-text-muted text-xs">未提供</td>
                  <td className="px-4 py-2.5 text-right text-text-muted text-xs">未提供</td>
                  <td className="px-4 py-2.5 text-right text-text-primary font-mono text-xs">{m.referenceLatencyMs === null ? "未提供" : `${m.referenceLatencyMs}ms`}</td>
                  <td className="px-4 py-2.5 text-right text-text-primary font-mono text-xs">{m.pricePer1kTokens === null ? "未提供" : <>{m.pricePer1kTokens.toFixed(4)}<br /><span className="text-[10px] text-text-muted">币种未提供</span></>}</td>
                  <td className="px-4 py-2.5 text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      <button onClick={() => navigate(`/forex/agents/model-config/detail?id=${m.id}`)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand transition-colors cursor-pointer whitespace-nowrap"><i className="ri-eye-line text-[11px]" />详情</button>
                      <button onClick={() => navigate("/forex/model-usage")} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand transition-colors cursor-pointer whitespace-nowrap" title="查看调用大盘"><i className="ri-bar-chart-line text-[11px]" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && filteredModels.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-text-muted">{remoteReady ? "暂无原子模型数据" : "模型列表加载失败，无法判断资源状态"}</div>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-[slideUp_0.3s_ease-out]">
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium text-white ${toast.type === "error" ? "bg-red-500" : "bg-emerald-500"}`}><i className={`${toast.type === "error" ? "ri-error-warning-fill" : "ri-checkbox-circle-fill"} text-base`} />{toast.msg}</div>
        </div>
      )}
    </div>
  );
}
